import React from 'react';
import portalCommunication from '@3plearning/mathletics-portal-communication-service';
import { DatePicker } from '@3plearning/react-components';

import skillQuestsCss from './skill-quests-mastery.scss';
import singleClassCss from './single-class/single-class.scss';
import ProgressBar from '../../components/progress-bar/progress-bar';
import {
    ASSIGNED_QUESTS,
    COMPLETED_QUESTS,
    COMPLETED_QUESTS_SUMMARY,
    EMPTY_CELL,
    STANDARDS_COMPLETED,
    STANDARDS_IN_PROGRESS,
    STUDENT_FULL_NAME
} from './skill-quests-mastery-constants';
import { SELECTED_DATE } from '../../components/report-filters/filter-constants';
import { PORTAL_MODULE } from '../../helpers/constants';
import { getConfiguration } from '../../helpers/utils';

/**
 * TEXT
 */
const text = {
    NO_STUDENTS_CLASS: 'Table Empty - No students in this class',
    NO_STUDENTS_GROUP: 'Table Empty - No students in this group',
    NO_STUDENTS_NO_SKILL_QUESTS_GROUP:
        'Table Empty - This group has no students and no skill quests course set',
    NO_SKILL_QUESTS: 'Table Empty - Skill Quests course not set',
    GO_TO_MANAGE_STUDENTS: 'Table Empty - Go to Manage Students',
    SKILL_QUESTS_NOT_SET_GROUPS:
        'Table Empty - Skill Quests course not set for any of the groups'
};

/**
 * HELPER FUNCTIONS
 */
export const getStudentFullname = (reportData) => {
    const { studentFirstName, studentLastName } = reportData;
    return `${studentFirstName} ${studentLastName}`;
};

const updateTableConfigNameById = (id, value, tableConfig) => {
    tableConfig.cols.find((col) => col.id === id).name = value;
    return tableConfig;
};

export const updateTableConfigTransformById = (id, value, tableConfig) => {
    tableConfig.cols.find((col) => col.id === id).transform = (row) =>
        value(row);
    return tableConfig;
};

const updateTableConfigNestedTransformById = (id, value, tableConfig) => {
    tableConfig.nestedData.cols.find((col) => col.id === id).transform = (
        row
    ) => value(row);
    return tableConfig;
};

const isUndefined = (value) => value === undefined;

export const manageHeaderValue = (value) => value.toString();

const getStandardsValue = (currentValue, totalValue) => {
    if (isUndefined(currentValue) || isUndefined(totalValue)) {
        return null;
    }

    return `${currentValue}/${totalValue}`;
};

const isEmptyArray = (value) => value && value.length === 0;

/**
 * EMPTY CELLS
 */
export const manageEmptyStudent = (row, defaultValue) => {
    const { isDataAvailable } = row;

    if (!isDataAvailable) return EMPTY_CELL;

    return defaultValue;
};

export const manageEmptyGroup = (row, defaultValue) => {
    const { groupCourseId, students } = row;
    const isStudentsEmpty = isEmptyArray(students);

    if (!groupCourseId || isStudentsEmpty) return EMPTY_CELL;

    return defaultValue;
};

export const manageEmptyClass = (row, defaultValue) => {
    const { isClassEmpty, areAllClassGroupsDefaultCoursesUndefined } = row;

    if (isClassEmpty || areAllClassGroupsDefaultCoursesUndefined)
        return EMPTY_CELL;

    return defaultValue;
};

/**
 * RENDER FUNCTIONS
 */
export const renderEmptyStateMessage = (
    portalModuleId,
    selectedClassId,
    labelText,
    i18n
) => (
    <div>
        {i18n._(labelText)} -{' '}
        <button
            className={skillQuestsCss['manage-students-button']}
            onClick={async () => {
                const moduleId = await getConfiguration(portalModuleId);
                portalCommunication.requestModuleChange(moduleId, {
                    classId: selectedClassId
                });
            }}
        >
            {i18n._(text.GO_TO_MANAGE_STUDENTS)}
        </button>
    </div>
);

const renderProgressBar = (row) => <ProgressBar {...row} />;

export const renderStandardsInProgress = (row) => {
    const { standardsInProgressCount, standardsAvailableCount } = row;

    return getStandardsValue(standardsInProgressCount, standardsAvailableCount);
};

export const renderStandardsCompleted = (row) => {
    const { standardsCompletedCount, standardsAvailableCount } = row;

    return getStandardsValue(standardsCompletedCount, standardsAvailableCount);
};

export const renderCompletedQuestsClassLevel = (row, i18n) => {
    const { isClassEmpty, classId, standardsAvailableCount } = row;
    const { NO_STUDENTS_CLASS, SKILL_QUESTS_NOT_SET_GROUPS } = text;

    if (isClassEmpty) {
        return renderEmptyStateMessage(
            PORTAL_MODULE.MANAGE_STUDENTS,
            classId,
            NO_STUDENTS_CLASS,
            i18n
        );
    }

    // check if Skill Quests are set for groups or not
    if (!standardsAvailableCount) {
        return renderEmptyStateMessage(
            PORTAL_MODULE.MANAGE_STUDENTS,
            classId,
            SKILL_QUESTS_NOT_SET_GROUPS,
            i18n
        );
    }

    return renderProgressBar(row);
};

export const renderEmptyState = (
    { groupCourseId, students },
    selectedClassId,
    i18n
) => {
    const {
        NO_STUDENTS_GROUP,
        NO_SKILL_QUESTS,
        NO_STUDENTS_NO_SKILL_QUESTS_GROUP
    } = text;
    let labelText = NO_SKILL_QUESTS;

    const isStudentEmpty = isEmptyArray(students);

    if (isStudentEmpty && groupCourseId === null) {
        labelText = NO_STUDENTS_NO_SKILL_QUESTS_GROUP;
    } else if (isStudentEmpty) {
        labelText = NO_STUDENTS_GROUP;
    }

    return renderEmptyStateMessage(
        PORTAL_MODULE.MANAGE_STUDENTS,
        selectedClassId,
        labelText,
        i18n
    );
};

export const renderCompletedQuestsGroupLevel = (row, selectedClassId, i18n) => {
    const { groupCourseName, groupPublicationName, students } = row;

    const isStudentEmpty = isEmptyArray(students);

    if (groupCourseName && !isStudentEmpty) {
        return <div>{`${groupPublicationName} - ${groupCourseName}`}</div>;
    }

    return renderEmptyState(row, selectedClassId, i18n);
};

export const renderStudentFullName = (row) => {
    const fullName = getStudentFullname(row);

    return (
        <div className={singleClassCss['student-full-name']} title={fullName}>
            {fullName}
        </div>
    );
};

/**
 * ALIGN TABLE CONFIGURATION
 */
export const alignTableConfigHeader = (data, tableConfig) => {
    const {
        learningJourneysAssignedOrInProgressCount,
        learningJourneysCompletedCount,
        standardsInProgressCount,
        standardsCompletedCount,
        standardsAvailableCount
    } = data;

    tableConfig = updateTableConfigTransformById(
        COMPLETED_QUESTS_SUMMARY.id,
        renderProgressBar,
        tableConfig
    );
    tableConfig = updateTableConfigNameById(
        ASSIGNED_QUESTS.id,
        manageHeaderValue(learningJourneysAssignedOrInProgressCount),
        tableConfig
    );
    tableConfig = updateTableConfigNameById(
        COMPLETED_QUESTS.id,
        manageHeaderValue(learningJourneysCompletedCount),
        tableConfig
    );
    tableConfig = updateTableConfigNameById(
        STANDARDS_IN_PROGRESS.id,
        renderStandardsInProgress({
            standardsInProgressCount,
            standardsAvailableCount
        }),
        tableConfig
    );
    tableConfig = updateTableConfigNameById(
        STANDARDS_COMPLETED.id,
        renderStandardsCompleted({
            standardsCompletedCount,
            standardsAvailableCount
        }),
        tableConfig
    );

    return tableConfig;
};

export const alignAllClassesTableConfigBody = (tableConfig, i18n) => {
    tableConfig = updateTableConfigTransformById(
        COMPLETED_QUESTS_SUMMARY.id,
        (row) => renderCompletedQuestsClassLevel(row, i18n),
        tableConfig
    );
    tableConfig = updateTableConfigTransformById(
        ASSIGNED_QUESTS.id,
        (row) => manageEmptyClass(row, row[ASSIGNED_QUESTS.id]),
        tableConfig
    );
    tableConfig = updateTableConfigTransformById(
        COMPLETED_QUESTS.id,
        (row) => manageEmptyClass(row, row[COMPLETED_QUESTS.id]),
        tableConfig
    );
    tableConfig = updateTableConfigTransformById(
        STANDARDS_IN_PROGRESS.id,
        (row) => manageEmptyClass(row, renderStandardsInProgress(row)),
        tableConfig
    );
    tableConfig = updateTableConfigTransformById(
        STANDARDS_COMPLETED.id,
        (row) => manageEmptyClass(row, renderStandardsCompleted(row)),
        tableConfig
    );

    return tableConfig;
};

export const alignSingleClassTableConfigBody = (tableConfig) => {
    tableConfig = updateTableConfigTransformById(
        STANDARDS_IN_PROGRESS.id,
        (row) => manageEmptyGroup(row, renderStandardsInProgress(row)),
        tableConfig
    );
    tableConfig = updateTableConfigTransformById(
        STANDARDS_COMPLETED.id,
        (row) => manageEmptyGroup(row, renderStandardsCompleted(row)),
        tableConfig
    );

    return tableConfig;
};

export const alignTableConfigNested = (tableConfig) => {
    tableConfig = updateTableConfigNestedTransformById(
        STUDENT_FULL_NAME.id,
        renderStudentFullName,
        tableConfig
    );
    tableConfig = updateTableConfigNestedTransformById(
        COMPLETED_QUESTS_SUMMARY.id,
        renderProgressBar,
        tableConfig
    );
    tableConfig = updateTableConfigNestedTransformById(
        STANDARDS_IN_PROGRESS.id,
        (row) => manageEmptyStudent(row, renderStandardsInProgress(row)),
        tableConfig
    );
    tableConfig = updateTableConfigNestedTransformById(
        STANDARDS_COMPLETED.id,
        (row) => manageEmptyStudent(row, renderStandardsCompleted(row)),
        tableConfig
    );
    tableConfig = updateTableConfigNestedTransformById(
        ASSIGNED_QUESTS.id,
        (row) => manageEmptyStudent(row, row[ASSIGNED_QUESTS.id]),
        tableConfig
    );
    tableConfig = updateTableConfigNestedTransformById(
        COMPLETED_QUESTS.id,
        (row) => manageEmptyStudent(row, row[COMPLETED_QUESTS.id]),
        tableConfig
    );

    return tableConfig;
};

/**
 * DATA HELPERS
 */
export const onInvalidRequest = (props, newProps, requestId) => {
    const { invalidRequest, updateActiveFilter } = props;
    const {
        activeFilters: newActiveFilters,
        invalidRequest: newInvalidRequest
    } = newProps;

    if (
        invalidRequest !== newInvalidRequest &&
        newInvalidRequest === requestId
    ) {
        if (
            newActiveFilters[SELECTED_DATE].type !== DatePicker.THIS_SCHOOL_YEAR
        ) {
            updateActiveFilter(SELECTED_DATE, {
                type: DatePicker.THIS_SCHOOL_YEAR
            });
        }
    }
};

/**
 * TRANSLATIONS
 */
export const manageTranslation = (tableConstant, i18n) => {
    const translationKey = `Table Config - ${tableConstant.name}`;

    return i18n._(translationKey);
};

export const translateTableContent = (i18n, tableConfig) => {
    tableConfig.cols.find(
        (col) => col.id === COMPLETED_QUESTS_SUMMARY.id
    ).name = manageTranslation(COMPLETED_QUESTS_SUMMARY, i18n);

    tableConfig.colGroups[0].find((col) =>
        col.colIds.includes(STANDARDS_IN_PROGRESS.id)
    ).name = manageTranslation(STANDARDS_IN_PROGRESS, i18n);

    tableConfig.colGroups[0].find((col) =>
        col.colIds.includes(STANDARDS_COMPLETED.id)
    ).name = manageTranslation(STANDARDS_COMPLETED, i18n);

    tableConfig.colGroups[0].find((col) =>
        col.colIds.includes(ASSIGNED_QUESTS.id)
    ).name = manageTranslation(ASSIGNED_QUESTS, i18n);

    tableConfig.colGroups[0].find((col) =>
        col.colIds.includes(COMPLETED_QUESTS.id)
    ).name = manageTranslation(COMPLETED_QUESTS, i18n);
};



// WEBPACK FOOTER //
// ./containers/skill-quests-mastery/skill-quests-mastery-helpers.js